import { Page, SimplePagination, Table } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { ampli } from 'lib-shared-tracking';
import { PureComponent, ReactNode } from 'react';

import { WithPresenceInjectedProps } from 'hocs';
import { PresenceFilter } from 'hocs/presence/withPresence';

import { TeamList } from '../containers/teamList';
import styles from '../teamPage.module.scss';

import { TeamHeader } from './teamHeader/teamHeader';

export const TEAM_NUMBER_OF_COLUMNS = 1;

export type TeamPageDispatchPropsT = {
  selectTeamMembersFilter: (string: string) => void;
  presenceGetAll: (userId: string) => Promise<unknown>;
  setPresenceFilters: (payload: {
    filter?: PresenceFilter;
    isAssignedOnly?: boolean;
    searchTerm?: string;
  }) => void;
};

export type TeamPageStatePropsT = {
  favourites: Array<string>;
  presenceFilters: {
    filter?: PresenceFilter;
    isAssignedOnly?: boolean;
    searchTerm?: string;
  };
};

type TeamPagePropsT = WithPresenceInjectedProps &
  TeamPageDispatchPropsT &
  TeamPageStatePropsT;

type TeamPageStateT = {
  isDropdownOpen: boolean;
  selectedSort: string;
};

export class TeamPage extends PureComponent<TeamPagePropsT, TeamPageStateT> {
  componentDidMount() {
    const { favourites } = this.props;
    const { filter, isAssignedOnly, searchTerm } = this.props.presenceFilters;

    if (searchTerm) {
      this.props.presence.provideSearchTerm(searchTerm);
    }

    if (typeof isAssignedOnly === 'boolean') {
      this.props.presence.provideAssignedOnly(isAssignedOnly);
    }

    if (filter) {
      this.props.presence.provideFilter(filter as PresenceFilter);
    }

    this.props.presence.queryItemsWithTotal(favourites);
  }

  componentDidUpdate(prevProps: TeamPagePropsT) {
    if (this.props.favourites !== prevProps.favourites) {
      this.props.presence.provideFavourites(this.props.favourites);
    }
  }

  @boundMethod
  onSearch(values: { team_search: string }): void {
    this.props.presence.searchTermChange(values.team_search);
    this.props.setPresenceFilters({
      ...this.props.presenceFilters,
      searchTerm: values.team_search,
    });
  }

  @boundMethod
  handleOnClickFilter(key: PresenceFilter): void {
    const { filterChange } = this.props.presence;
    filterChange(key);
    this.props.setPresenceFilters({
      ...this.props.presenceFilters,
      filter: key,
    });

    ampli.teamFilterMenuItemClicked({ type: key });
  }

  @boundMethod
  onNextPageClicked(): void {
    this.props.presence.nextPage();
    ampli.teamNextPageClicked();
  }

  @boundMethod
  onPreviousPageClicked(): void {
    this.props.presence.previousPage();
    ampli.teamPreviousPageClicked();
  }

  @boundMethod
  onAssignedOnlyClicked(): void {
    this.props.presence.assignedFilterChange();
    this.props.setPresenceFilters({
      ...this.props.presenceFilters,
      isAssignedOnly: !this.props.presence.isAssignedOnly,
    });
    ampli.teamAssignedAgentsOnlyClicked();
  }

  @boundMethod
  onSearchClose(): void {
    this.props.presence.searchTermChange('');
    this.props.setPresenceFilters({
      ...this.props.presenceFilters,
      searchTerm: '',
    });
  }

  renderFoot(): ReactNode {
    const { hasNextPage, hasPreviousPage, itemsAsyncStatus, items } =
      this.props.presence;

    const hasPagination = items.length > 0 && (hasNextPage || hasPreviousPage);

    if (!hasPagination) {
      return null;
    }

    return (
      <Table.Footer>
        <SimplePagination
          previousButtonClick={this.onPreviousPageClicked}
          previousButtonDisabled={!hasPreviousPage}
          nextButtonClick={this.onNextPageClicked}
          nextButtonDisabled={!hasNextPage}
          loading={itemsAsyncStatus === 'loading'}
        />
      </Table.Footer>
    );
  }

  render() {
    const { filter, isAssignedOnly, total, itemsAsyncStatus } =
      this.props.presence;

    return (
      <Page>
        <TeamHeader
          isAssignedOnly={isAssignedOnly}
          filter={filter}
          itemsAsyncStatus={itemsAsyncStatus}
          total={total}
          searchTerm={this.props.presenceFilters.searchTerm}
          onAssignedOnlyClicked={this.onAssignedOnlyClicked}
          onSearchClose={this.onSearchClose}
          onFilterClick={this.handleOnClickFilter}
          onSearch={this.onSearch}
        />

        <Page.Body>
          <Table
            framed={false}
            className={styles.table}
            wrapperProps={{ className: styles.tableWrapper }}
          >
            <Table.Tbody>
              <TeamList presence={this.props.presence} />
            </Table.Tbody>

            {this.renderFoot()}
          </Table>
        </Page.Body>
      </Page>
    );
  }
}
